import { accountNumber } from './canonicalFields';

/**
 * Per-account nicknames live on users.account_defaults[accountId].nickname
 * (same JSONB used for risk / isPaperTrading — no new tables).
 */

function accountDefaultsEntry(accountDefaults, accountId) {
  if (!accountDefaults || accountId == null) return null;
  const id = String(accountId);
  return (
    accountDefaults[id] ||
    accountDefaults[`${id}_paper`] ||
    accountDefaults[`${id}_live`] ||
    null
  );
}

/** @returns {string|null} trimmed nickname or null */
export function getAccountNickname(accountDefaults, accountId) {
  const entry = accountDefaultsEntry(accountDefaults, accountId);
  const raw = entry?.nickname;
  if (raw == null) return null;
  const trimmed = String(raw).trim();
  return trimmed || null;
}

/**
 * Label for UI chrome (headers, dropdowns, confirmations).
 * Prefer user nickname; otherwise AccountID / account number.
 * Does NOT use Margin/Futures type — we don't always know it reliably.
 *
 * @param {object|string|number|null} account - account object or raw account id
 * @param {object|null|undefined} accountDefaults - user.accountDefaults / accountSettings
 * @param {{ preferId?: boolean }} [opts] - preferId: always show broker id (orders tables, etc.)
 */
export function getAccountDisplayLabel(account, accountDefaults, opts = {}) {
  if (account == null || account === '') return '';
  const id =
    typeof account === 'string' || typeof account === 'number'
      ? String(account)
      : accountNumber(account) || account.AccountID;
  if (opts.preferId) return id != null ? String(id) : '';
  const nick = getAccountNickname(accountDefaults, id);
  if (nick) return nick;
  return id != null ? String(id) : '';
}

/**
 * Merge a nickname into account_defaults for the given account id.
 * Empty / whitespace (or nickname equal to the raw account id) clears the nickname.
 */
export function withAccountNickname(accountDefaults, accountId, nickname) {
  const id = String(accountId);
  const next = { ...(accountDefaults || {}) };
  const prev = { ...(next[id] || {}) };
  const trimmed = nickname == null ? '' : String(nickname).trim();
  if (trimmed && trimmed !== id) {
    prev.nickname = trimmed;
  } else {
    delete prev.nickname;
  }
  if (Object.keys(prev).length === 0) {
    delete next[id];
  } else {
    next[id] = prev;
  }
  return next;
}
